import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import * as styles from './Tooltip.module.css';

export const Tooltip = React.memo(
  ({
    children,
    content,
    position = 'top',
    showOnHover = true,
    showOnClick = false,
    delay = 300,
    className = '',
    pointerOffset = 0,
  }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [isPositioned, setIsPositioned] = useState(false);
    const triggerRef = useRef(null);
    const tooltipRef = useRef(null);
    const timeoutRef = useRef(null);

    const calculatePosition = useCallback(() => {
      if (!triggerRef.current || !tooltipRef.current) return;

      const triggerRect = triggerRef.current.getBoundingClientRect();
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      let top;
      let left;

      let contentRect = triggerRect;
      const childElements = Array.from(triggerRef.current.children);
      const contentChild = childElements.find((child) => {
        const childRect = child.getBoundingClientRect();
        return childRect.width > 0 && childRect.height > 0;
      });

      if (contentChild) {
        const childRect = contentChild.getBoundingClientRect();
        contentRect = {
          top: childRect.top,
          left: childRect.left,
          width: childRect.width,
          height: childRect.height,
          bottom: childRect.bottom,
          right: childRect.right,
        };
      }

      switch (position) {
        case 'top':
          top = contentRect.top - tooltipRect.height - 8;
          left = contentRect.left + pointerOffset - tooltipRect.width / 2;
          break;
        case 'bottom':
          top = contentRect.bottom + 8;
          left = contentRect.left + pointerOffset - tooltipRect.width / 2;
          break;
        case 'left':
          top =
            contentRect.top + contentRect.height / 2 - tooltipRect.height / 2;
          left = contentRect.left - tooltipRect.width - 8;
          break;
        case 'right':
          top =
            contentRect.top + contentRect.height / 2 - tooltipRect.height / 2;
          left = contentRect.right + 8;
          break;
        default:
          top = contentRect.top - tooltipRect.height - 8;
          left = contentRect.left + pointerOffset - tooltipRect.width / 2;
      }

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top < 8) top = 8;
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = viewportHeight - tooltipRect.height - 8;
      }

      setTooltipPosition({ top, left });
      setIsPositioned(true);
    }, [position, pointerOffset]);

    const handleMouseEnter = () => {
      if (showOnHover) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          setIsVisible(true);
          setTimeout(() => {
            calculatePosition();
          }, 20);
        }, delay);
      }
    };

    const handleMouseLeave = () => {
      if (showOnHover) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        setIsVisible(false);
        setIsPositioned(false);
      }
    };

    const handleClick = () => {
      if (showOnClick) {
        if (!isVisible) {
          setIsVisible(true);
          setTimeout(() => {
            calculatePosition();
          }, 20);
        } else {
          setIsVisible(false);
          setIsPositioned(false);
        }
      }
    };

    const handleKeyDown = (e) => {
      if (showOnClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        handleClick();
      }
    };

    useEffect(() => {
      if (isVisible) {
        const handleResize = () => {
          calculatePosition();
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', calculatePosition);

        return () => {
          window.removeEventListener('resize', handleResize);
          window.removeEventListener('scroll', calculatePosition);
        };
      }
      return undefined;
    }, [isVisible, calculatePosition]);

    useEffect(() => {
      if (!isVisible) return undefined;

      const handleClickOutside = (e) => {
        if (
          showOnClick &&
          triggerRef.current &&
          !triggerRef.current.contains(e.target) &&
          tooltipRef.current &&
          !tooltipRef.current.contains(e.target)
        ) {
          setIsVisible(false);
          setIsPositioned(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }, [isVisible, showOnClick]);

    useEffect(
      () => () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      },
      [],
    );

    const getPointerClass = () => {
      switch (position) {
        case 'top':
          return styles['tooltip-pointer-top'];
        case 'bottom':
          return styles['tooltip-pointer-bottom'];
        case 'left':
          return styles['tooltip-pointer-left'];
        case 'right':
          return styles['tooltip-pointer-right'];
        default:
          return styles['tooltip-pointer-top'];
      }
    };

    const renderTrigger = () => {
      if (showOnClick) {
        return (
          <button
            ref={triggerRef}
            className={className}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            type="button"
          >
            {children}
          </button>
        );
      }

      return (
        <div
          ref={triggerRef}
          className={className}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}
        </div>
      );
    };

    return (
      <>
        {renderTrigger()}

        {isVisible &&
          ReactDOM.createPortal(
            <div
              ref={tooltipRef}
              className={`${styles.tooltip} ${getPointerClass()}`}
              style={{
                top: tooltipPosition.top,
                left: tooltipPosition.left,
                opacity: isPositioned ? 1 : 0,
                transition: 'opacity 0.1s ease-in-out',
              }}
            >
              <div className={styles['tooltip-content']}>{content}</div>
            </div>,
            document.body,
          )}
      </>
    );
  },
);

